<template>
    <div class="lead-generation-page">
        <h2>{{ $options.TITLE }}</h2>

        <div class="actions">
            <div class="search-bar">
                <ds-input-field
                    v-model="searchInput"
                    :label="$options.SEARCH_LABEL"
                >
                    <ds-icon slot="leading" name="search" />
                </ds-input-field>
            </div>

            <ds-dropdown
                disable-scroll
                icons
                data-qa="actions-dropdown"
                :is-open="isActionDropdownOpen"
                @close="closeQuickActions"
            >
                <template #default>
                    <ds-filled-button :trailing-icon="chevronIcon" @click="toggleActionDropdown">
                        <div>{{ $options.ACTION_BUTTON }}</div>
                    </ds-filled-button>
                </template>

                <template #menu>
                    <div slot="menu" class="dropdown-menu-list">
                        <div class="dropdown-menu-item" @click="showModal">
                            <ds-icon name="code" />
                            {{ $options.TRACKING_CODE }}
                        </div>
                        <div class="dropdown-menu-item" @click="createLeadSource">
                            <ds-icon name="add" />
                            {{ $options.CREATE_LEAD_SOURCE }}
                        </div>
                    </div>
                </template>
            </ds-dropdown>
        </div>

        <lead-generation-cards
            v-for="block in leadGenEntries"
            :key="block.title"
            data-qa="lead-generation-cards"
            :block-configuration="block"
            :visitor-search-link="visitorSearchLink"
            :lead-source-search-link="leadSourceSearchLink"
            :lead-source-effectiveness-report-link="leadSourceEffectivenessReportLink"
            :lead-source-report-link="leadSourceReportLink"
            :lead-source-category-report-link="leadSourceCategoryReportLink"
            :lead-source-count="leadSourceCount"
        />

        <tracking-code-modal
            ref="trackingCode"
            :tracking-code-modal-title="trackingCodeModalTitle"
        />

        <div v-if="leadGenEntries.length === 0" class="no-result">
            <h3>{{ $options.NO_RESULTS }}</h3>
        </div>

        <lead-generation-warning-modal
            ref="LeadWarningModal"
        />
    </div>
</template>

<script>
import leadGenerationEntries from './leadGenerationEntries.json';
import LeadGenerationCards from './LeadGenerationCards';
import TrackingCodeModal from './TrackingCodeModal';
import LeadGenerationWarningModal from './LeadGenerationWarningModal';

export const TITLE = 'Lead Generation';
export const SEARCH_LABEL = 'Search Lead Generation';
export const QUICK_ACTIONS_LABEL = 'Quick actions';
export const NO_RESULTS = 'No results';
export const ACTION_BUTTON = 'Quick actions';
export const TRACKING_CODE = 'Get Tracking Code';
export const CREATE_LEAD_SOURCE = 'Create Lead Source';
export const CREATE_LEAD_SOURCE_LINK = '/LeadSource/manageLeadSource.jsp?view=add';


export default {
    leadGenerationEntries,
    TITLE,
    SEARCH_LABEL,
    QUICK_ACTIONS_LABEL,
    NO_RESULTS,
    ACTION_BUTTON,
    TRACKING_CODE,
    CREATE_LEAD_SOURCE,
    CREATE_LEAD_SOURCE_LINK,

    components: {
        LeadGenerationCards,
        TrackingCodeModal,
        LeadGenerationWarningModal,
    },

    props: {
        trackingCodeModalTitle: String,
        visitorSearchLink: String,
        leadSourceSearchLink: String,
        leadSourceEffectivenessReportLink: String,
        leadSourceReportLink: String,
        leadSourceCategoryReportLink: String,
        leadSourceCount: String,
    },

    data() {
        return {
            searchInput: '',
            isActionDropdownOpen: false,
            warningMsgLeadsource: false,
            warningMsg: false,
        };
    },

    mounted() {
        this.$root.$on('TRACKING_CODE_MODEL', this.showModal);
        this.$root.$on('WARNING_LEAD_SOURCE', this.LeadWarnShowModal);
        this.$root.$on('ADD_LEAD_SOURCE', this.openAddleadSource);
    },

    beforeDestroy() {
        this.$root.$off('TRACKING_CODE_MODEL', this.showModal);
        this.$root.$off('WARNING_LEAD_SOURCE', this.LeadWarnShowModal);
        this.$root.$off('ADD_LEAD_SOURCE', this.openAddleadSource);
    },

    computed: {
        leadGenEntries() {
            const search = this.searchInput?.trim()?.toLowerCase();

            if (search !== '') {
                let filteredBlocks = this.$options.leadGenerationEntries.map((block) => {
                    const { columns } = block;

                    let filteredColumns = columns.map((column) => {
                        const { cards } = column;
                        let filteredCards = cards.map((card) => {
                            const filteredEntries = card.entries.filter(({ title, description }) => {
                                return title.toLowerCase().includes(search)
                                        || description.toLowerCase().includes(search);
                            });

                            if (filteredEntries.length > 0) {
                                return {
                                    id: card.id,
                                    footer: card.footer,
                                    link: card.link,
                                    entries: filteredEntries,
                                };
                            }

                            return false;
                        });

                        filteredCards = filteredCards.filter((card) => {
                            return card;
                        });

                        if (filteredCards.length > 0) {
                            return {
                                id: column.id,
                                cards: filteredCards,
                            };
                        }

                        return false;
                    });

                    filteredColumns = filteredColumns.filter((column) => {
                        return column;
                    });

                    if (filteredColumns.length > 0) {
                        return {
                            title: block.title,
                            columns: filteredColumns,
                        };
                    }

                    return false;
                });

                filteredBlocks = filteredBlocks.filter((block) => {
                    return block;
                });

                return filteredBlocks;
            }

            return this.$options.leadGenerationEntries;
        },

        chevronIcon() {
            return this.isActionDropdownOpen ? 'chevron-up' : 'chevron-down';
        },
    },
    methods: {
        toggleActionDropdown() {
            this.isActionDropdownOpen = !this.isActionDropdownOpen;
        },

        closeQuickActions() {
            this.isActionDropdownOpen = false;
        },

        createLeadSource() {
            if (this.leadSourceCount > 50000) {
                this.$refs.LeadWarningModal.showModal();
            } else {
                window.open(this.$options.CREATE_LEAD_SOURCE_LINK, '_self');
            }
        },

        openAddleadSource() {
            window.open(this.$options.CREATE_LEAD_SOURCE_LINK, '_self');
        },

        showModal() {
            this.closeQuickActions();
            this.$refs.trackingCode.showModal();
        },

        LeadWarnShowModal() {
            this.$refs.LeadWarningModal.showModal();
        },
    },
};
</script>

<style  lang="scss" scoped>
.actions {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    margin: 20px 0 24px 0;
    justify-items:  left;
    justify-content: space-between;
}

.lead-generation-page {
    min-height: 100%;
    margin-top: $spacing-400;
    justify-content: space-between;
}

@media screen and ($medium) {
    .search-bar {
        width: 35%;
    }
}

</style>
